import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polygon, Circle, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { api } from "@/lib/api";
import {
  ISTRES_PARKINGS, ISTRES_KIOSKS, ISTRES_DEFIBRILLATORS,
  ISTRES_CENTER,
} from "@/lib/istres";
import { WORLD_BOUNDARY } from "@/lib/istres_boundary";
import {
  MapPin, ParkingCircle, Info, Heart, Store, ArrowRight, Sparkles,
} from "lucide-react";

// Rayon "périmètre Istres" — 5 km autour du centre-ville
const RADIUS_M = 5000;

// Approxime le cercle en 64 points pour le mask (polygon avec trou circulaire)
function circlePolygon(centerLat, centerLng, radiusMeters, points = 64) {
  const earthRadius = 6378137;
  const latRad = (centerLat * Math.PI) / 180;
  const out = [];
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dLat = (radiusMeters * Math.cos(angle)) / earthRadius;
    const dLng = (radiusMeters * Math.sin(angle)) / (earthRadius * Math.cos(latRad));
    out.push([centerLat + (dLat * 180) / Math.PI, centerLng + (dLng * 180) / Math.PI]);
  }
  return out;
}

function makeIcon(color, svgInner, size = 30) {
  const html = `
    <div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};color:#fff;
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 4px 14px rgba(15,46,81,0.45);
      border:2px solid #fff;
    ">
      <svg width="${size * 0.55}" height="${size * 0.55}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">${svgInner}</svg>
    </div>`;
  return L.divIcon({
    className: "istres-spot-marker",
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const ICON_MERCHANT = makeIcon("#5B3A6B", '<path d="M3 9l1-5h16l1 5"/><path d="M5 9v10a2 2 0 002 2h10a2 2 0 002-2V9"/><path d="M9 22V12h6v10"/>', 28);
const ICON_PARKING  = makeIcon("#3B82C4", '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M9 17V7h4a3 3 0 010 6H9"/>', 26);
const ICON_KIOSK    = makeIcon("#F5B400", '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12" y2="16"/>', 24);
const ICON_DAE      = makeIcon("#E5856A", '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/>', 24);

const LAYER_DEFS = [
  { id: "merchants", label: "Commerçants", color: "#5B3A6B", icon: Store },
  { id: "parkings",  label: "Parkings",    color: "#3B82C4", icon: ParkingCircle },
  { id: "kiosks",    label: "Bornes info", color: "#F5B400", icon: Info },
  { id: "dae",       label: "Défibrillateurs", color: "#E5856A", icon: Heart },
];

function FitToIstres() {
  const map = useMap();
  useEffect(() => {
    map.setView([ISTRES_CENTER.lat, ISTRES_CENTER.lng], 14, { animate: false });
  }, [map]);
  return null;
}

export default function IstresSpotlightMap() {
  const [merchants, setMerchants] = useState([]);
  const [active, setActive] = useState({ merchants: true, parkings: true, kiosks: true, dae: false });

  // Cercle "spotlight" autour d'Istres
  const istresRing = useMemo(() => circlePolygon(ISTRES_CENTER.lat, ISTRES_CENTER.lng, RADIUS_M), []);

  useEffect(() => {
    api.get("/public/merchants").then((r) => {
      const arr = (r.data?.items || r.data || [])
        .filter((m) => typeof m.lat === "number" && typeof m.lng === "number");
      setMerchants(arr);
    }).catch(() => setMerchants([]));
  }, []);

  const counts = useMemo(() => ({
    merchants: merchants.length,
    parkings: ISTRES_PARKINGS.length,
    kiosks: ISTRES_KIOSKS.length,
    dae: ISTRES_DEFIBRILLATORS.length,
  }), [merchants.length]);

  return (
    <section
      className="bg-gradient-to-br from-plum-900 via-plum-800 to-plum-900 text-cream-50 relative overflow-hidden"
      data-testid="istres-spotlight-map"
    >
      <div className="absolute -top-32 right-0 w-[40rem] h-[40rem] rounded-full bg-sun-400/8 blur-[140px] pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-32 left-0 w-[36rem] h-[36rem] rounded-full bg-pink-500/8 blur-[140px] pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
        {/* Header */}
        <div className="grid lg:grid-cols-[1fr_auto] items-end gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sun-400/15 text-sun-300 text-[11px] font-bold uppercase tracking-[0.18em] mb-4 border border-sun-300/20">
              <Sparkles size={11} strokeWidth={2.5} /> Carte interactive
            </div>
            <h2 className="font-display font-extrabold text-[2rem] sm:text-[2.6rem] lg:text-[3rem] leading-[1.05]">
              Istres en un coup d&apos;œil,
              <br className="hidden sm:block" />
              <span className="text-sun-300">tout ce qui compte au même endroit.</span>
            </h2>
            <p className="text-[15px] text-cream-50/75 mt-3 max-w-2xl leading-relaxed">
              Commerces ouverts, parkings disponibles, bornes d&apos;information et défibrillateurs — la ville mise en lumière, en temps réel.
            </p>
          </div>
          <Link
            to="/plan"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-sun-400 hover:bg-sun-300 text-plum-900 px-5 h-12 font-bold text-[14px] shadow-lg shadow-sun-400/25 transition-transform hover:-translate-y-0.5"
            data-testid="goto-fullmap"
          >
            Carte des parkings &amp; infrastructures <ArrowRight size={14} />
          </Link>
        </div>

        {/* Layer toggles */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="map-layer-toggles">
          {LAYER_DEFS.map((l) => {
            const isOn = active[l.id];
            const Icon = l.icon;
            return (
              <button
                key={l.id}
                onClick={() => setActive((a) => ({ ...a, [l.id]: !a[l.id] }))}
                data-testid={`toggle-${l.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-bold transition-all ${
                  isOn
                    ? "bg-white text-plum-900 shadow-md"
                    : "bg-white/8 text-cream-50/70 border border-white/15 hover:bg-white/15"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${isOn ? "" : "opacity-40"}`} style={{ background: l.color }} />
                <Icon size={12} strokeWidth={2.4} />
                {l.label}
                <span className={`text-[10px] font-mono px-1.5 rounded-full ${isOn ? "bg-cream-100 text-plum-700" : "bg-white/10"}`}>
                  {counts[l.id]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Map */}
        <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-plum-900/50">
          <MapContainer
            center={[ISTRES_CENTER.lat, ISTRES_CENTER.lng]}
            zoom={14}
            style={{ height: 540, width: "100%", background: "#0f1d2e" }}
            scrollWheelZoom={false}
            attributionControl={true}
          >
            {/* Base tile : OSM clair pour l'intérieur d'Istres (visible dans le "trou" du mask) */}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Mask : couvre TOUT le monde sauf un rond autour d'Istres → spotlight circulaire */}
            <Polygon
              positions={[WORLD_BOUNDARY, istresRing]}
              pathOptions={{
                color: "transparent",
                fillColor: "#0a1322",
                fillOpacity: 0.85,
                weight: 0,
                fillRule: "evenodd",
                interactive: false,
              }}
            />

            {/* Cercle de mise en lumière */}
            <Circle
              center={[ISTRES_CENTER.lat, ISTRES_CENTER.lng]}
              radius={RADIUS_M}
              pathOptions={{
                color: "#FFD500",
                weight: 1.5,
                opacity: 0.7,
                fillOpacity: 0,
                dashArray: "6 6",
              }}
            />

            {/* Markers — clic = zoom */}
            {active.parkings && ISTRES_PARKINGS.map((p) => (
              <Marker
                key={p.id}
                position={[p.lat, p.lng]}
                icon={ICON_PARKING}
                eventHandlers={{ click: (e) => e.target._map.flyTo([p.lat, p.lng], 16, { animate: true, duration: 0.6 }) }}
              >
                <Popup>
                  <div className="text-[13px]">
                    <strong className="block mb-1">🅿 {p.name}</strong>
                    {p.type} · {p.capacity} places<br />
                    <span className="text-plum-700/70">{p.hours}</span>
                  </div>
                </Popup>
              </Marker>
            ))}

            {active.kiosks && ISTRES_KIOSKS.map((k) => (
              <Marker
                key={k.id}
                position={[k.lat, k.lng]}
                icon={ICON_KIOSK}
                eventHandlers={{ click: (e) => e.target._map.flyTo([k.lat, k.lng], 16, { animate: true, duration: 0.6 }) }}
              >
                <Popup>
                  <div className="text-[13px]">
                    <strong className="block mb-1">ℹ {k.name}</strong>
                    <span className="text-plum-700/70">{k.hours}</span>
                  </div>
                </Popup>
              </Marker>
            ))}

            {active.dae && ISTRES_DEFIBRILLATORS.map((d) => (
              <Marker
                key={d.id}
                position={[d.lat, d.lng]}
                icon={ICON_DAE}
                eventHandlers={{ click: (e) => e.target._map.flyTo([d.lat, d.lng], 16, { animate: true, duration: 0.6 }) }}
              >
                <Popup>
                  <div className="text-[13px]">
                    <strong className="block mb-1">❤ {d.name}</strong>
                    <span className="text-plum-700/70">{d.access}</span>
                  </div>
                </Popup>
              </Marker>
            ))}

            {active.merchants && (
              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                spiderfyOnMaxZoom={true}
                maxClusterRadius={50}
                disableClusteringAtZoom={17}
                iconCreateFunction={(cluster) => {
                  const c = cluster.getChildCount();
                  const size = c < 10 ? 36 : c < 100 ? 44 : 52;
                  return L.divIcon({
                    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#5B3A6B;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:${size > 44 ? 15 : 13}px;border:3px solid #fff;box-shadow:0 4px 18px rgba(15,46,81,0.45);">${c}</div>`,
                    className: "istres-cluster",
                    iconSize: [size, size],
                  });
                }}
              >
                {merchants.map((m) => (
                  <Marker
                    key={m.id}
                    position={[m.lat, m.lng]}
                    icon={ICON_MERCHANT}
                    eventHandlers={{ click: (e) => e.target._map.flyTo([m.lat, m.lng], 17, { animate: true, duration: 0.6 }) }}
                  >
                    <Popup>
                      <div className="text-[13px] max-w-[220px]">
                        <strong className="block mb-1">{m.merchant_name || m.name}</strong>
                        {m.category && <span className="block text-plum-700/65 text-[11px] uppercase tracking-wider font-bold">{m.category}</span>}
                        {m.description && <span className="block mt-1 text-plum-700/75">{m.description}</span>}
                        {m.address && <span className="block text-[11px] mt-1 text-plum-700/55">{m.address}</span>}
                        {m.source === "registered" && (
                          <Link to={`/commercants/${m.id}`} className="text-mint-700 font-bold text-[12px] mt-1 inline-block">
                            Voir la fiche →
                          </Link>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}

            <FitToIstres />
          </MapContainer>

          {/* Légende flottante */}
          <div className="absolute bottom-4 left-4 right-4 sm:left-4 sm:right-auto pointer-events-none">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-plum-900/85 backdrop-blur-md text-cream-50 text-[11px] font-semibold pointer-events-auto border border-white/10">
              <MapPin size={11} className="text-sun-300" />
              <span>Périmètre Istres mis en lumière</span>
            </div>
          </div>
        </div>

        {/* CTA mobile */}
        <Link
          to="/plan"
          className="sm:hidden mt-4 inline-flex w-full justify-center items-center gap-2 rounded-full bg-sun-400 hover:bg-sun-300 text-plum-900 h-12 font-bold text-[14px]"
        >
          Carte des parkings &amp; infrastructures <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}
